// src/components/PivaObbligatoria.js
// Partita IVA dell'azienda dopo la registrazione (utils/pivaOrganizzazione.js):
//  • prima della scadenza: fascia in alto per titolare/amministratori con i giorni rimasti;
//  • dopo: fascia rossa per tutti — si consulta ma non si modifica — e il titolare la
//    inserisce dalla finestra (si apre da sola una volta per sessione).
// La finestra si apre anche quando si prova a invitare il primo collega senza P.IVA
// (evento "nexiora:richiedi-piva").
import React, { useEffect, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { statoPiva, erroreFormatoPiva, normalizzaPiva, GIORNI_PIVA } from "../utils/pivaOrganizzazione";
import { useFornitore } from "../utils/fornitore";

const VIOLA = "#7c3aed";

export function richiediPiva(motivo = "") {
  window.dispatchEvent(new CustomEvent("nexiora:richiedi-piva", { detail: { motivo } }));
}

export default function PivaObbligatoria() {
  const { isOrgAdmin, isPlatformOwner, userProfile } = useAuth();
  const { orgVisitata, effectiveOrgId } = useOrg();
  const fornitore = useFornitore();
  const [aperta, setAperta] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [valore, setValore] = useState("");
  const [errore, setErrore] = useState("");
  const [salvo, setSalvo] = useState(false);
  const [mostrataAuto, setMostrataAuto] = useState(false);

  const s = statoPiva(orgVisitata);
  const titolare = !!(isOrgAdmin || ["titolare", "admin"].includes(userProfile?.role));

  useEffect(() => {
    const h = (e) => { setMotivo(e.detail?.motivo || ""); setErrore(""); setAperta(true); };
    window.addEventListener("nexiora:richiedi-piva", h);
    return () => window.removeEventListener("nexiora:richiedi-piva", h);
  }, []);
  // scaduto il termine, al titolare la finestra si apre da sola (una volta per sessione)
  useEffect(() => {
    if (s.bloccata && titolare && !isPlatformOwner && !mostrataAuto) { setMostrataAuto(true); setMotivo(""); setAperta(true); }
  }, [s.bloccata, titolare, isPlatformOwner, mostrataAuto]);

  if (!s.mancante) return null;

  const salva = async () => {
    const err = erroreFormatoPiva(valore);
    if (err) { setErrore(err); return; }
    setSalvo(true); setErrore("");
    try {
      await httpsCallable(getFunctions(), "impostaPivaOrganizzazione")({ orgId: effectiveOrgId, piva: normalizzaPiva(valore) });
      setAperta(false);
    } catch (e) {
      const codice = e?.details?.codice || "";
      setErrore(codice === "duplicata"
        ? `Azienda già registrata su Nexiora. Contatta il supporto: ${fornitore.emailSupporto || "support@nexiora.it"}`
        : (e?.message && !/internal/i.test(e.message) ? e.message : "Non è stato possibile salvare: riprova tra poco."));
    }
    setSalvo(false);
  };

  const fascia = s.bloccata
    ? { bg: "#fef2f2", bordo: "#fecaca", col: "#991b1b", testo: titolare
        ? "Inserisci la Partita IVA dell'azienda per continuare a lavorare: fino ad allora i dati si possono consultare ma non modificare."
        : "Il titolare deve completare i dati dell'azienda (Partita IVA) per continuare: fino ad allora i dati si possono consultare ma non modificare." }
    : { bg: "#f5f3ff", bordo: "#ddd6fe", col: "#5b21b6", testo: `Completa i dati dell'azienda: inserisci la Partita IVA entro ${s.giorniRimasti === 1 ? "1 giorno" : `${s.giorniRimasti} giorni`}.` };
  if (!s.bloccata && !titolare) return null; // prima della scadenza il promemoria è solo per chi può inserirla

  return (
    <>
      <div style={{ background: fascia.bg, borderBottom: `1px solid ${fascia.bordo}`, color: fascia.col, padding: "7px 16px", fontSize: 12.5, fontWeight: 600, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ flex: 1, minWidth: 220 }}>🏢 {fascia.testo}</span>
        {titolare && <button onClick={() => { setMotivo(""); setErrore(""); setAperta(true); }}
          style={{ background: VIOLA, color: "#fff", border: "none", borderRadius: 7, padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Inserisci Partita IVA</button>}
      </div>
      {aperta && titolare && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && !s.bloccata && setAperta(false)}>
          <div className="modal" style={{ maxWidth: 440 }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: "#1e1b4b", margin: "0 0 6px" }}>Partita IVA dell'azienda</h3>
            {motivo && <div style={{ fontSize: 12.5, color: "#fff", background: VIOLA, borderRadius: 8, padding: "7px 10px", marginBottom: 10, fontWeight: 600 }}>{motivo}</div>}
            <p style={{ fontSize: 12.5, color: "#4b5563", margin: "0 0 12px", lineHeight: 1.5 }}>
              Serve per le intestazioni dei documenti e per la fatturazione. Si inserisce una volta sola: Nexiora controlla che sia valida e
              che non sia già registrata da un'altra azienda. Per cambiarla in seguito si contatta l'assistenza.
              {!s.bloccata && <> Va inserita entro {GIORNI_PIVA} giorni dalla registrazione.</>}
            </p>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#374151" }}>Partita IVA (o Codice Fiscale per le ditte individuali)</label>
            <input className="input" autoFocus value={valore} onChange={e => { setValore(e.target.value); setErrore(""); }}
              onKeyDown={e => { if (e.key === "Enter") salva(); }} placeholder="Es. 01234567890" maxLength={20} style={{ marginTop: 4 }} />
            {errore && <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 6 }}>{errore}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
              {!s.bloccata && <button className="btn btn-secondary" onClick={() => setAperta(false)}>Più tardi</button>}
              {s.bloccata && <button className="btn btn-secondary" onClick={() => setAperta(false)}>Solo consultazione</button>}
              <button className="btn btn-primary" onClick={salva} disabled={salvo}>{salvo ? "Verifico…" : "Salva"}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
